import {
  Modal,
  Box,
  Grid,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  DIALOG_BOX_STYLE,
  TEXT_OVERFLOW_1LINE,
} from '../../../_constants/styleConst';
import { GetNgSample } from '../../../services/apiTypes';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import {
  addImage,
  selectImagesByKey,
} from '../../../stores/ngSampleImages/slice';
import React, { useEffect, useState } from 'react';
import { getBlobData } from '../../../services/api/BlobData';
import { NG_SAMPLE_CONTAINER_NAME } from '../../../_constants/inspectionConst';
import { isGetBlobData } from '../../../_utils/typeGuardUtils';
import { API_CODE } from '../../../_constants/apiConst';
import useErrorModal from '../../../hooks/useErrorModal';

type NgSampleModalProps = {
  isOpen: boolean;
  ngSampleList: GetNgSample[];
  handleClose: () => void;
};

const NgSampleModal: React.FC<NgSampleModalProps> = ({
  isOpen,
  ngSampleList,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  const sampleDataList = useSelector(selectImagesByKey(typeId));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { typeLabelsMap } = useSelector((state: RootState) => state.typeLabels);
  const selectedType = typeLabelsMap[categoryId].find(
    (typeData) => typeData.id === typeId,
  );
  const { getApiErrorMessage, getTypeErrorMessage } = useErrorModal();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        if (
          sampleDataList.length === 0 &&
          ngSampleList.length !== 0 &&
          selectedType
        ) {
          Promise.all(
            ngSampleList.map(async (ngSample) => {
              const response = await getBlobData({
                containerName: NG_SAMPLE_CONTAINER_NAME,
                path: ngSample.path,
              });

              if (!isGetBlobData(response)) {
                setError(getTypeErrorMessage(API_CODE.BLOB_DATA));
              }

              for (const item of response) {
                dispatch(
                  addImage({
                    key: typeId,
                    image: item.data,
                    path: item.path,
                  }),
                );
              }
            }),
          ).catch((error) => {
            setError(getApiErrorMessage(error, API_CODE.BLOB_DATA));
          });
        }
      } catch (error) {
        setError(getApiErrorMessage(error, API_CODE.BLOB_DATA));
        return;
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [ngSampleList]);

  return (
    <React.Fragment>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            ...DIALOG_BOX_STYLE,
            width: '90%',
            maxWidth: 1200,
            height: 'auto',
            maxHeight: '90dvh',
            p: 2,
          }}
        >
          <Typography id="modal-title" variant="h6" component="h2">
            {t('LBL_NG_SAMPLE')}
          </Typography>
          <Typography id="modal-body" variant="h6" component="h3">
            <Box sx={{ overflow: 'auto', maxHeight: '60dvh', mt: 2 }}>
              <Grid container spacing={2}>
                {[...ngSampleList]
                  .sort((prev, next) => prev.index - next.index)
                  .map((item, i) => {
                    const src = sampleDataList.find(
                      (sample) => sample.path === item.path,
                    )?.image;
                    return (
                      <Grid
                        key={`ng_sample_${i}`}
                        size={4}
                        display="flex"
                        flexDirection="column"
                      >
                        <Typography
                          sx={[
                            {
                              fontSize: 'clamp(12px, 2vw, 18px)',
                            },
                            TEXT_OVERFLOW_1LINE,
                          ]}
                        >
                          {item.label}
                        </Typography>
                        {isLoading ? (
                          <CircularProgress
                            key={`ng_sample_loading_${i}`}
                            color="inherit"
                          />
                        ) : error !== '' || src === '' ? (
                          <Typography color="error">{error}</Typography>
                        ) : (
                          <img
                            key={`ng_sample_image_${i}`}
                            src={src}
                            alt="background"
                            width="360px"
                            height="360px"
                          />
                        )}
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
            <Grid direction="row-reverse" container spacing={2}>
              <Grid size={3} sx={{ paddingTop: 3, paddingRight: 2 }}>
                <Button onClick={handleClose} variant="outlined" fullWidth>
                  {t('BTN_CANCEL')}
                </Button>
              </Grid>
            </Grid>
          </Typography>
        </Box>
      </Modal>
    </React.Fragment>
  );
};

export default NgSampleModal;
